@if (plan(); as p) {
  <div
    class="balance-payment-plan-summary"
    data-testid="payment-plan-summary"
    fxLayout="column"
    fxLayoutGap="1rem"
  >
    <header
      class="balance-payment-plan-summary__header"
      fxLayout="row"
      fxLayoutAlign="space-between center"
      fxLayoutGap=".75rem"
    >
      <div
        class="balance-payment-plan-summary__title-group"
        fxLayout="row"
        fxLayoutAlign="start center"
        fxLayoutGap=".5rem"
      >
        <mat-icon class="balance-payment-plan-summary__title-icon">event_repeat</mat-icon>
        <span class="balance-payment-plan-summary__title">
          {{ 'balance.payment-plan.active-title' | translate }}
        </span>
      </div>
      <mat-chip
        class="balance-payment-plan-summary__chip"
        color="primary"
        [removable]="false"
        [disableRipple]="true"
      >
        {{ 'balance.payment-plan.active' | translate }}
      </mat-chip>
    </header>

    @if (failedCharge(); as failed) {
      <div
        class="balance-payment-plan-summary__alert"
        data-testid="payment-plan-failed-charge"
        role="alert"
        fxLayout="row"
        fxLayoutAlign="start center"
        fxLayoutGap=".5rem"
      >
        <mat-icon class="balance-payment-plan-summary__alert-icon">error_outline</mat-icon>
        <div fxLayout="column">
          <strong>{{ 'balance.payment-plan.charge-failed' | translate }}</strong>
          <span class="balance-payment-plan-summary__alert-detail">
            {{ 'balance.payment-plan.charge-failed-detail' | translate: {
              date: failed.createdAt | date:'mediumDate',
              nextChargeAt: (nextChargeAt() | date:'mediumDate') || ('balance.payment-plan.charge-failed-pending' | translate)
            } }}
          </span>
        </div>
      </div>
    }

    <div
      class="balance-payment-plan-summary__stats mb-5"
      fxLayout="row"
      fxLayout.lt-md="column"
    >
      <div
        class="balance-payment-plan-summary__stat m-0"
        fxFlex
        fxLayout="row"
        fxLayoutAlign="start center"
        fxLayoutGap="0.5rem"
      >
        <span
          class="balance-payment-plan-summary__stat-glyph"
          aria-hidden="true"
          fxFlex="0 0 auto"
          fxLayout="row"
          fxLayoutAlign="center center"
        >
          <mat-icon>event_repeat</mat-icon>
        </span>
        <div class="balance-payment-plan-summary__stat-text" fxLayout="column">
          <span class="balance-payment-plan-summary__stat-label">
            {{ 'balance.payment-plan.installments' | translate }}
          </span>
          <span class="balance-payment-plan-summary__stat-value">
            {{ 'balance.payment-plan.installments-value' | translate: { paid: installmentsPaid(), total: p.months } }}
          </span>
        </div>
      </div>

      <div
        class="balance-payment-plan-summary__stat m-0"
        fxFlex
        fxLayout="row"
        fxLayoutAlign="start center"
        fxLayoutGap="0.5rem"
      >
        <span
          class="balance-payment-plan-summary__stat-glyph"
          aria-hidden="true"
          fxFlex="0 0 auto"
          fxLayout="row"
          fxLayoutAlign="center center"
        >
          <mat-icon>payments</mat-icon>
        </span>
        <div class="balance-payment-plan-summary__stat-text" fxLayout="column">
          <span class="balance-payment-plan-summary__stat-label">
            {{ 'balance.payment-plan.monthly-amount' | translate }}
          </span>
          <span class="balance-payment-plan-summary__stat-value">
            {{ p.monthlyAmount | currency:'USD' }}
          </span>
          @if (hasDistinctFirstPayment()) {
            <span
              class="balance-payment-plan-summary__stat-hint"
              data-testid="payment-plan-first-payment"
            >
              {{ 'balance.payment-plan.first-payment-hint' | translate: { amount: (firstPayment() | currency:'USD') } }}
            </span>
          }
        </div>
      </div>

      <div
        class="balance-payment-plan-summary__stat m-0"
        fxFlex
        fxLayout="row"
        fxLayoutAlign="start center"
        fxLayoutGap="0.5rem"
      >
        <span
          class="balance-payment-plan-summary__stat-glyph"
          aria-hidden="true"
          fxFlex="0 0 auto"
          fxLayout="row"
          fxLayoutAlign="center center"
        >
          <mat-icon>event_available</mat-icon>
        </span>
        <div class="balance-payment-plan-summary__stat-text" fxLayout="column">
          <span class="balance-payment-plan-summary__stat-label">
            {{ 'balance.payment-plan.next-charge' | translate }}
          </span>
          <span class="balance-payment-plan-summary__stat-value">
            @if (nextChargeAt(); as next) {
              {{ next | date:'mediumDate' }}
            } @else {
              {{ 'balance.payment-plan.next-charge-none' | translate }}
            }
          </span>
        </div>
      </div>
    </div>

    <div
      class="balance-payment-plan-summary__duration"
      data-testid="payment-plan-duration"
      fxLayout="column"
      fxLayoutGap=".625rem"
    >
      <div
        class="balance-payment-plan-summary__duration-header"
        fxLayout="row"
        fxLayoutAlign="space-between center"
        fxLayoutGap=".5rem"
      >
        <span class="balance-payment-plan-summary__duration-label">
          {{ 'balance.payment-plan.duration' | translate }}
        </span>
        <span class="balance-payment-plan-summary__duration-progress">
          {{ 'balance.payment-plan.duration-progress' | translate: { percent: progressPercent() } }}
        </span>
      </div>

      <div
        class="balance-payment-plan-summary__duration-track"
        role="progressbar"
        [attr.aria-valuemin]="0"
        [attr.aria-valuemax]="100"
        [attr.aria-valuenow]="progressPercent()"
      >
        <span
          class="balance-payment-plan-summary__duration-fill"
          [style.width.%]="progressPercent()"
        ></span>
      </div>

      <div
        class="balance-payment-plan-summary__duration-endpoints"
        fxLayout="row"
        fxLayoutAlign="space-between center"
        fxLayoutGap=".5rem"
      >
        <div class="balance-payment-plan-summary__endpoint" fxLayout="column">
          <span class="balance-payment-plan-summary__endpoint-label">
            {{ 'balance.payment-plan.started-at' | translate }}
          </span>
          <span class="balance-payment-plan-summary__endpoint-value">
            {{ p.startedAt | date:'mediumDate' }}
          </span>
        </div>
        <div
          class="balance-payment-plan-summary__endpoint balance-payment-plan-summary__endpoint--end"
          fxLayout="column"
        >
          <span class="balance-payment-plan-summary__endpoint-label">
            {{ 'balance.payment-plan.ends-at' | translate }}
          </span>
          <span class="balance-payment-plan-summary__endpoint-value">
            @if (endsAt(); as ends) {
              {{ ends | date:'mediumDate' }}
            } @else {
              —
            }
          </span>
        </div>
      </div>
    </div>
  </div>
}
